import { Slot } from "@radix-ui/react-slot";
import { cva, VariantProps } from "class-variance-authority";
import * as React from "react";

import { classNames } from "$app/utils/classNames";

import { ButtonColor } from "$app/components/design";

export const brandNames = [
  "paypal",
  "discord",
  "stripe",
  "facebook",
  "twitter",
  "apple",
  "android",
  "kindle",
  "zoom",
  "google",
] as const;

export type BrandName = (typeof brandNames)[number];

export const buttonVariants = cva(
  // Hover drop-shadow is pinned to black (var(--color-black)) in BOTH light and dark mode.
  // The default `shadow` token resolves to `currentColor`, which flips to near-white in dark
  // mode (text color becomes rgb(221 221 221)) and renders a strange white button shadow.
  // Matching light mode's black keeps the neobrutalist look consistent across color schemes.
  "inline-flex items-center justify-center gap-2 cursor-pointer border border-border rounded text-current font-[inherit] no-underline transition-transform hover:-translate-1 hover:shadow-[0.25rem_0.25rem_0_var(--color-black)] active:translate-0 active:shadow-none data-[state=open]:translate-0! data-[state=open]:shadow-none!",
  {
    variants: {
      variant: {
        default: "",
        outline: "bg-transparent",
        secondary: "",
        destructive: "",
      },
      size: {
        default: "px-4 py-3 text-base leading-snug",
        sm: "p-2 text-sm leading-[1.3]",
        icon: "size-12 box-border",
      },
      color: {
        default: "bg-transparent",
        primary: "bg-primary text-primary-foreground hover:bg-accent-with-text hover:text-accent-foreground",
        black: "bg-black text-white",
        accent: "bg-accent-with-text text-accent-foreground",
        filled: "bg-background text-foreground",
        success: "bg-success text-white",
        danger: "bg-danger text-white",
        warning: "bg-warning text-black",
        info: "bg-primary text-primary-foreground",
        paypal: "bg-[#00457c] text-white border-[#00457c]",
        discord: "bg-[#7289da] text-white border-[#7289da]",
        stripe: "bg-[#625bf6] text-white border-[#625bf6]",
        facebook: "bg-[#4267b2] text-white border-[#4267b2]",
        twitter: "bg-black text-white",
        apple: "bg-black text-white",
        android: "bg-[#142f40] text-white",
        kindle: "bg-[#f3a642] text-black border-[#f3a642]",
        zoom: "bg-[#4087fc] text-white border-[#4087fc]",
        google: "bg-[#5383ec] text-white border-[#5383ec]",
      },
    },
    compoundVariants: [
      {
        variant: "outline",
        color: "primary",
        className: "bg-transparent text-current hover:bg-black hover:text-white",
      },
      {
        variant: "outline",
        color: "danger",
        className: "bg-transparent text-current hover:bg-danger hover:text-white",
      },
      {
        variant: "outline",
        color: "success",
        className: "bg-transparent text-current hover:bg-success hover:text-white",
      },
      {
        variant: "outline",
        color: "warning",
        className: "bg-transparent text-current hover:bg-warning hover:text-black",
      },
      {
        variant: "outline",
        color: "info",
        className: "bg-transparent text-current hover:bg-primary hover:text-primary-foreground",
      },
      {
        variant: "outline",
        color: "black",
        className: "bg-transparent text-current hover:bg-black hover:text-white",
      },
      {
        variant: "outline",
        color: "accent",
        className: "bg-transparent text-current hover:bg-accent-with-text hover:text-accent-foreground",
      },
      {
        variant: "outline",
        color: "filled",
        className: "bg-transparent text-black hover:bg-white hover:text-black",
      },
    ],
    defaultVariants: {
      variant: "default",
      size: "default",
      color: "default",
    },
  },
);

type ButtonVariation = {
  color?: ButtonColor | BrandName | undefined;
  outline?: boolean | undefined;
};

export interface ButtonProps extends Omit<React.ComponentPropsWithoutRef<"button">, "color">, ButtonVariation {
  size?: VariantProps<typeof buttonVariants>["size"];
  asChild?: boolean;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, color, outline, size, disabled, children, asChild = false, ...props }, ref) => {
    const variant = outline ? "outline" : color === "danger" ? "destructive" : "default";

    const classes = classNames(
      buttonVariants({ variant, size, color: color || undefined }),
      disabled && "cursor-not-allowed opacity-30 hover:translate-0 hover:shadow-none",
      className,
    );
    const Comp = asChild ? Slot : "button";

    return (
      <Comp className={classes} ref={ref} disabled={disabled} type={asChild ? undefined : "button"} {...props}>
        {children}
      </Comp>
    );
  },
);
Button.displayName = "Button";

export interface NavigationButtonProps extends Omit<React.ComponentPropsWithoutRef<"a">, "color">, ButtonVariation {
  size?: VariantProps<typeof buttonVariants>["size"];
  disabled?: boolean | undefined;
}

export const NavigationButton = React.forwardRef<HTMLAnchorElement, NavigationButtonProps>(
  ({ className, color, outline, size, disabled, children, ...props }, ref) => (
    <Button asChild className={className} color={color} outline={outline} size={size} disabled={disabled}>
      <a
        ref={ref}
        inert={disabled}
        {...props}
        onClick={(evt) => {
          if (props.onClick == null) return;

          if (props.href == null || props.href === "#") evt.preventDefault();

          props.onClick(evt);

          evt.stopPropagation();
        }}
      >
        {children}
      </a>
    </Button>
  ),
);
NavigationButton.displayName = "NavigationButton";
